import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { Router } from '@angular/router'
import { HubspotService } from '@app/services/hubspot.service'
import { RepositoriesService } from '@app/services/repositories.service'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService, LegalInfo } from '@auth/services/auth.service'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbSpinnerModule } from '@nebular/theme'
import { BillingService } from '@payments/services/billing.service'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

/**
 * Workspace organization information component
 */
@Component({
  selector: 'ngx-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
  imports: [CommonModule, NbCardModule, NbFormFieldModule, NbSelectModule, MatIconModule, NbButtonModule, NbInputModule, NbSpinnerModule]
})
export class OrganizationComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Organization legal info model
   */
  legalInfo: LegalInfo = null
  /**
   * Whether the info are submittable
   */
  hasValidLegalInfo = false
  /**
   * Is the component ready
   */
  ready = false
  /**
   * List of stripe countries
   */
  countries: any[]
  /**
   * Is it a stripe country
   */
  isCountryValid = true
  /**
   * Is the country among the list
   */
  existCountryCode: string
  /**
   * List of available entity types selection
   */
  entityTypes: { value: number; name: string }[] = [
    { name: 'Individual /sole proprietorship.', value: 1 },
    { name: 'Company', value: 2 },
    { name: 'Non profit type', value: 3 },
    { name: 'Government entity type', value: 4 }
  ]

  /**
   * Organization details form group
   */
  organizationForm: UntypedFormGroup
  /**
   * FormControl for autocomplete
   */
  myControl = new UntypedFormControl()
  /**
   * IndustrySector Observable
   */
  industrySectors$: Observable<any[]>
  /**
   * Creates the component and injects it's dependencies
   * @param fb FormBuilder
   * @param router Router
   * @param repositoriesService RepositoriesService
   * @param auth AuthService
   * @param toastr ToastrService
   * @param paymentService BillingService
   * @param hubspot HubspotService
   */
  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private repositoriesService: RepositoriesService,
    private auth: AuthService,
    private toastr: ToastrService,
    private paymentService: BillingService,
    private hubspot: HubspotService
  ) {}

  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * If the workspace is connected to a stripe account
   */
  get isConnected() {
    return this.auth.currentWorkspace.can_collect_payment
  }
  /**
   * If the legal infor has a country selected
   */
  get hasCountry() {
    return !!this.legalInfo?.country
  }
  /**
   * If the payments for the workspace should be handled by stripe
   */
  get showStripeConnection() {
    return !this.auth.currentWorkspace.options?.payment || this.auth.currentWorkspace.options?.payment?.gateway === 'stripe'
  }
  /**
   * Validates wrokspace legal information
   */
  validateLegalInfo() {
    this.hasValidLegalInfo =
      !!this.legalInfo &&
      // !!this.legalInfo.company_address &&
      !!this.legalInfo.company_email &&
      !!this.legalInfo.company_name &&
      !!this.legalInfo.company_type &&
      // !!this.legalInfo.industry_sector_id &&
      // !!this.legalInfo.company_phone &&
      !!this.legalInfo.country
  }
  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.ready = false
    this.industrySectors$ = this.auth.getIndustrySectors()
    if (!this.auth.canEditOrganization) {
      this.router.navigateByUrl('app-settings/profile')
    }
    this.countries = this.repositoriesService.CountriesTimezonesRepository

    this.auth
      .getOrganizationInfo()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: LegalInfo) => {
        this.legalInfo = res
        this.validateLegalInfo()
        this.organizationForm = this.fb.group({
          workspace_id: [res.workspace_id ?? this.auth.currentParentWorkspace.id],
          country: [res.country ?? '', [Validators.required]],
          company_type: [res.company_type ?? null],
          company_name: [res.company_name ?? ''],
          company_email: [res.company_email ?? '', [Validators.email]]
        })
        this.isCountryValid = !!this.repositoriesService.validStripeCountries.find((o) => o.code === res.country)
        this.ready = true
      })
  }

  /**
   * Updates the workspace legal info
   */
  save() {
    this.ready = false

    const formValue: LegalInfo = Object.assign({}, this.organizationForm.value)
    Object.entries(formValue).forEach((o) => {
      if (this.organizationForm.controls[o[0]].pristine && o[0] != 'workspace_id') {
        delete formValue[o[0]]
      } else formValue[o[0]] = typeof o[1] === 'number' ? o[1] : o[1].trim() ? o[1] : null
    })

    this.auth
      .updateOrganization(formValue)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Workspace) => {
          this.toastr.success('Updated organization information', '')
          this.ngOnInit()
        },
        () => {
          this.toastr.error('Failed to update organization information', '')
          this.ready = true
        }
      )
  }

  /**
   * Redirects the user to stripe to complete his stripe connect
   */
  redirectToStripe() {
    this.paymentService
      .getStripeLink()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        window.open(res.url, '_blank')
      })
  }
  /**
   * Sends an email to MICEtribe for a user that's not part of the countries supported by stripe connect
   */
  requestActivation() {
    const emailBody =
      'Hi MICEtribe team ' +
      escape('\n') +
      'I would like to activate payment collection for my workspace ' +
      (this.auth.currentParentWorkspace?.name || this.auth.currentWorkspace.name)
    const url = `mailto:help@contactless.io?subject=Request Activation&body=${emailBody}`
    window.location.href = url
  }

  filter(val: string): Observable<any[]> {
    return this.auth.getIndustrySectors().pipe(
      map((response: any) =>
        response.filter((option) => {
          return option.label.toLowerCase().indexOf(val.toLowerCase()) === 0
        })
      )
    )
  }

  requestOtherPaymentProvider() {
    this.validateLegalInfo()
    if (this.hasValidLegalInfo) {
      this.hubspot
        .openTicket(this.auth.currentWorkspace.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.toastr.success(
              'A new payment methode was requested',
              'Your request has been received, please allow us up to 72 hours to review your request and enable payment collection for you.'
            )
          },
          (err) => {
            this.toastr.error('Error', err.message)
          }
        )
    } else {
      this.toastr.error('Error', 'Please fill and save all the organization information fields before proceeding.')
    }
  }
}
